import {
  CaretUpFilled,
  FilterFilled,
  PlusOutlined,
  QuestionCircleFilled,
} from '@ant-design/icons';
import { Alert, Badge, Button, Space, Tabs, Tooltip, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ContentCard from '../../components/ContentCard';
import { Permission } from '../../user/types/Permission';
import { useUser } from '../../user/userContext';
import { userHasPermission } from '../../user/userUtils';
import OpenClassAPI from '../OpenClassAPI';
import DateOpenClassesModal from '../components/DateOpenClassesModal';
import OpenClassFilter from '../components/OpenClassFilter';
import OpenClassCalendar from '../components/OpenClassCalendar';
import OpenClassCatalog from '../components/OpenClassCatalog';
import OpenClassFormModal from '../components/OpenClassFormModal';
import OpenClassViewInstructions from '../components/OpenClassViewInstructions';
import { useCourses } from '../hooks/useCourses';
import { OpenClass } from '../types/OpenClass';
import { openClassCalendarDays } from '../utils/openClassCalendar';
import {
  OpenClassFilterValues,
  filterOpenClasses,
} from '../utils/openClassFilter';

const OpenClasses: React.FC = () => {
  const { t } = useTranslation();
  const [currentUser] = useUser();
  const { canReadCourses, courses } = useCourses();
  const [openClasses, setOpenClasses] = useState<OpenClass[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<Dayjs | null>(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterPristine, setFilterPristine] = useState(true);
  const [filter, setFilter] = useState<OpenClassFilterValues>({});

  const onFilterChange = useCallback(
    (values: OpenClassFilterValues, pristine: boolean) => {
      setFilter(values);
      setFilterPristine(pristine);
    },
    [],
  );

  const visibleOpenClasses = useMemo(
    () => filterOpenClasses(openClasses, filter),
    [filter, openClasses],
  );

  const calendarDays = useMemo(
    () => openClassCalendarDays(visibleOpenClasses),
    [visibleOpenClasses],
  );

  const canCreate = userHasPermission(
    currentUser,
    Permission.COURSE_INSTANCE_CREATE,
  );

  const scopedToOwnSessions = !userHasPermission(
    currentUser,
    Permission.COURSE_INSTANCE_READ_GLOBAL,
  );

  const refreshOpenClasses = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await OpenClassAPI.getOpenClasses();
      setOpenClasses(data);
    } catch {
      message.error(t('views.OpenClasses.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    refreshOpenClasses();
  }, [refreshOpenClasses]);

  return (
    <>
      <div className="mb-5 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">
          {t('views.OpenClasses.title')}
        </h1>
        <Space size="small">
          <Tooltip
            placement="top"
            title={t('views.CourseInstances.instructions')}>
            <Button
              aria-label={t('views.CourseInstances.instructions')}
              icon={<QuestionCircleFilled />}
              shape="circle"
              type="primary"
              onClick={() => setShowInstructions(true)}
            />
          </Tooltip>
          <Badge dot={!filterPristine}>
            <Tooltip placement="top" title={t('common.filter')}>
              <Button
                aria-label={t('common.filter')}
                icon={filterVisible ? <CaretUpFilled /> : <FilterFilled />}
                shape="circle"
                type="primary"
                onClick={() => setFilterVisible((visible) => !visible)}
              />
            </Tooltip>
          </Badge>
          {canCreate && canReadCourses && (
            <Button
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => setCreateModalOpen(true)}>
              {t('views.OpenClasses.addOpenClass')}
            </Button>
          )}
        </Space>
      </div>
      <OpenClassFilter
        courses={courses}
        visible={filterVisible}
        onChange={onFilterChange}
      />
      {scopedToOwnSessions && (
        <Alert
          className="mb-4"
          message={t('views.OpenClasses.scopedToOwnSessions')}
          showIcon
          type="info"
        />
      )}
      <ContentCard>
        <Tabs
          defaultActiveKey="calendar"
          size="large"
          items={[
            {
              children: (
                <OpenClassCalendar
                  fetchedEvents={calendarDays}
                  loadingOpenClasses={loading}
                  onClickDay={(date) => setSelectedDay(date && dayjs(date))}
                />
              ),
              key: 'calendar',
              label: t('views.OpenClasses.calendar'),
            },
            {
              children: (
                <OpenClassCatalog
                  loading={loading}
                  openClasses={visibleOpenClasses}
                  refreshOpenClasses={refreshOpenClasses}
                />
              ),
              key: 'list',
              label: t('views.OpenClasses.list'),
            },
          ]}
        />
      </ContentCard>
      {selectedDay && (
        <DateOpenClassesModal
          date={selectedDay}
          loading={loading}
          openClasses={visibleOpenClasses}
          refreshOpenClasses={refreshOpenClasses}
          onCancel={() => setSelectedDay(null)}
        />
      )}
      <OpenClassViewInstructions
        visible={showInstructions}
        onCancel={() => setShowInstructions(false)}
      />
      <OpenClassFormModal
        courses={courses}
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={refreshOpenClasses}
      />
    </>
  );
};

export default OpenClasses;
